/**
 * Peasant's lazy loading
 *
 * <iframes, img> using data-on-scroll-src instead of src attribute
 * start loading when user scroll the page.
 *
 * **How to use:**
 *   * populate your lazy `img` or `iframe` elements with a `data-on-scroll-src`
 *     attribute but without the `src` attribute
 *   * to make use the plugin in your component and use the register and destroy functions
 *   * once the element is in view, the src attribute will be populated,
 *     hence the content from src will be visible
 *   * whenever needed, execute the register function
 *   * on unmounted it is advised to use the destroy function
 *
 * **Tips:**
 *   * pre define your `iframe`s and `img`s with a fixed width and height to make sure that
 *     once the source is loaded, there will be no layout shifts
 *
 * TODO: expand with custom root scrollable container instead of document, if necessary.
 *
 * @returns `setupLazyLoading`, `initObservers` and the corresponding `destroyLazyLoading` functions
 */
export const useLazyLoading = () => {
  let observer: IntersectionObserver = null;
  let lazyElements: Array<Element> = [];

  const getLazyElements = () => {
    const elements = [];
    ['img', 'iframe'].forEach((element) => {
      document.querySelectorAll(`${element}[data-on-scroll-src]`).forEach((innerElement) => {
        elements.push(innerElement);
      });
    });

    return elements;
  };

  /**
   * Initializes lazy loading elements after timeout of 300ms.
   */
  const setupLazyLoading = () => {
    setTimeout(() => {
      initObservers();
    }, 300);
  };

  /**
   * Initializes lazy loading elements.
   */
  const initObservers = () => {
    destroyLazyLoading();

    lazyElements = getLazyElements();
    observer = new IntersectionObserver((observerEntries) => {
      observerEntries.forEach((entry) => {
        if (entry.isIntersecting) {
          const lazyElement = entry.target as HTMLElementWithSrc;
          lazyElement.src = lazyElement.dataset.onScrollSrc;
          lazyElement.removeAttribute('data-on-scroll-src');
          observer.unobserve(lazyElement);
        }
      });
    });

    lazyElements.forEach((lazyElement) => {
      observer.observe(lazyElement);
    });
  };

  /**
   * Destroys everything lazy loading related.
   */
  const destroyLazyLoading = () => {
    if (observer != null) {
      lazyElements.forEach((lazyElement) => {
        observer.unobserve(lazyElement);
      });

      observer.disconnect();
      observer = null;
      lazyElements.length = 0;
    }
  };

  return { setupLazyLoading, destroyLazyLoading, initObservers };
};

/**
 * Expand HTMLElement with src attribute instead of union type of Image and IFrame
 */
declare interface HTMLElementWithSrc extends HTMLElement {
  src: string;
}
